/**
 * Centralized, code-level entity content overrides for local debugging.
 *
 * This is the single place to override any entity's content on the client. Every
 * entity fetched from the server — via GET/`_mget` and via ES `query` — funnels
 * through `Store.materializeRecord`, which looks up an override here (keyed by the
 * entity's `uniqueId`) and applies it before the entity is materialized. Nothing is
 * ever written back to the server, so you can test how the app renders/behaves
 * against edited content without touching a single row.
 *
 * Configure by adding entries to {@link ENTITY_DEBUG_OVERRIDES}:
 *
 *   export const ENTITY_DEBUG_OVERRIDES = {
 *     // bare content object = deep-merged into whatever the server returns:
 *     '67cac340-2662-4184-a3c1-b7a3251d7d68': {
 *       core_integrations_sftp_ingestion_config: {
 *         sourceDirectory: '/debug/inbound',
 *         messageType: 'MULTI_ENTITY',
 *       },
 *     },
 *     // wrap in replace(...) to swap the whole content instead of merging:
 *     'd5b620b2-d06a-11f0-913f-eb45c5559ec4': replace({ ...fullContent }),
 *   }
 *
 * Keep this map EMPTY on commit — it's a scratchpad for local work, not config
 * that should ship.
 */

export interface IEntityDebugOverride {
  /** Partial content to deep-merge (mode `merge`) or the full content (mode `replace`). */
  content: any
  /** `merge` deep-merges `content` into the server payload; `replace` swaps it wholesale. */
  mode: 'merge' | 'replace'
}

/** A map entry: a bare content object (deep-merged) or an explicit {@link IEntityDebugOverride}. */
export type EntityDebugOverrideEntry = IEntityDebugOverride | Record<string, any>

/** Deep-merge `content` into the entity's server payload (this is also the default for bare objects). */
export const merge = (content: any): IEntityDebugOverride => ({ mode: 'merge', content })

/** Replace the entity's content wholesale (its `uniqueId` is preserved). */
export const replace = (content: any): IEntityDebugOverride => ({ mode: 'replace', content })

/**
 * uniqueId -> override. Add entries here to override entities locally.
 * Keep empty when committing.
 */
export const ENTITY_DEBUG_OVERRIDES: Record<string, EntityDebugOverrideEntry> = {}
export const ENTITY_DEBUG_OVERRIDES_X: Record<string, EntityDebugOverrideEntry> = {
  // '<entity-uniqueId>': { <namespace>: { <field>: <value> } },

  // [VD-24640] Local preview of the Multi Entity Payload Config detail section before the schema's
  // uiSchema change is deployed. Merges uiSchema onto the payload config schema entity.
  '62ed12d4-0419-11f0-a333-f783787e5a87': {
    metadata: { isCloneable: true },
    uiSchema: {
      web: {
        entityDetailSection: {
          type: 'ui:view',
          children: [
            {
              type: 'ui:section',
              title: 'Entities',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText:
                    'Entity aliases ingested from each payload row, each mapped to an entity type with per-alias ingestion flags.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:dictionary',
                  value: 'core_integrations_ingestion_multiEntity_payloadConfig.entities',
                  keyLabel: 'Entity alias',
                  valueLabel: 'Entity Spec',
                  items: {
                    type: 'ui:formTable',
                    className: 'c-dictionaryEntry',
                    children: [
                      {
                        type: 'ui:dictionaryKey',
                        className: 'c-dictionaryKey',
                        style: { width: 200 },
                        itemTemplate: {
                          type: 'ui:input',
                          placeholder: 'Entity alias',
                        },
                      },
                      {
                        type: 'ui:dictionaryValue',
                        className: 'c-dictionaryValue',
                        previewCard: {
                          type: 'ui:view',
                        },

                        itemTemplate: {
                          type: 'ui:embeddedSheet',
                          value: '.',
                          sheetTitle: 'Entity Spec',
                          placeholder: 'Click to configure entity spec',
                          previewCard: {
                            type: 'ui:view',
                          },
                          preview: {
                            type: 'ui:view',
                            className: 'c-entity-preview-card c-entity-preview-card--isClickable',
                            style: {
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            },
                            children: [
                              {
                                type: 'ui:view',
                                children: [
                                  {
                                    type: 'ui:view',
                                    className: 'c-entity-preview-card--title',
                                    mapStateToProps: {
                                      text: { type: 'expression', value: "JOIN(mixins, ', ')" },
                                    },
                                  },
                                  {
                                    type: 'ui:view',
                                    style: { opacity: 0.6 },
                                    mapStateToProps: { text: 'ingestionFlags.writeBehavior' },
                                  },
                                ],
                              },
                              { type: 'ui:icon', name: 'edit' },
                            ],
                          },
                          children: [
                            {
                              type: 'ui:section',
                              title: 'Entity Type',
                              isCollapsable: true,
                              children: [
                                {
                                  type: 'ui:helpBlock',
                                  helpText:
                                    'Entity type(s) this alias maps to — schema namespace URIs (e.g. /1.0/entities/metadata/core_yms_spot.json).',
                                  className: 'u-bumperBottom--sm',
                                },
                                {
                                  type: 'ui:formulaInput',
                                  isVisible: false,
                                  formula: 'ENTITY_TYPE_URIS()',
                                  transientValue: '_entityTypeUris',
                                },
                                {
                                  type: 'ui:list',
                                  value: 'mixins',
                                  addButtonText: 'Add Entity Type',
                                  showAddButton: true,
                                  items: {
                                    type: 'ui:selectField',
                                    value: '.',
                                    isCreatable: true,
                                    isSearchable: true,
                                    placeholder: 'Entity type namespace URI',
                                    mapStateToProps: { suggestions: '_entityTypeUris' },
                                  },
                                  mapActionsToProps: {
                                    createNewItem: { type: 'expression', value: "''" },
                                  },
                                },
                              ],
                            },
                            {
                              type: 'ui:formTable',
                              children: [
                                {
                                  label: 'Required',
                                  type: 'ui:checkboxField',
                                  value: 'ingestionFlags.isRequired',
                                },
                                {
                                  label: 'Write Behavior',
                                  type: 'ui:selectField',
                                  value: 'ingestionFlags.writeBehavior',
                                },
                                {
                                  label: 'Dedupe Type',
                                  type: 'ui:inputField',
                                  value: 'ingestionFlags.dedupeType',
                                  placeholder: 'Override deduper schema URI (optional)',
                                },
                              ],
                            },
                            {
                              type: 'ui:section',
                              title: 'Roll-Up Paths',
                              isCollapsable: true,
                              children: [
                                {
                                  type: 'ui:helpBlock',
                                  helpText:
                                    'JSON paths to roll up (collapse repeated child rows into arrays) when uploading.',
                                  className: 'u-bumperBottom--sm',
                                },
                                {
                                  type: 'ui:list',
                                  value: 'ingestionFlags.rollups',
                                  addButtonText: 'Add Rollup Path',
                                  showAddButton: true,
                                  items: {
                                    type: 'ui:input',
                                    value: '.',
                                    placeholder: 'JSON path to roll up',
                                  },
                                  mapActionsToProps: {
                                    createNewItem: { type: 'expression', value: "''" },
                                  },
                                },
                              ],
                            },
                            {
                              type: 'ui:section',
                              title: 'Clear When Empty',
                              isCollapsable: true,
                              children: [
                                {
                                  type: 'ui:helpBlock',
                                  helpText:
                                    'Dot-paths to clear on the existing entity when the incoming value is null, empty, or absent (applied post-merge).',
                                  className: 'u-bumperBottom--sm',
                                },
                                {
                                  type: 'ui:list',
                                  value: 'ingestionFlags.unsetIfNullOrEmpty',
                                  addButtonText: 'Add Path',
                                  showAddButton: true,
                                  items: {
                                    type: 'ui:input',
                                    value: '.',
                                    placeholder: 'Dot-path to clear (e.g. address.street)',
                                  },
                                  mapActionsToProps: {
                                    createNewItem: { type: 'expression', value: "''" },
                                  },
                                },
                              ],
                            },
                            {
                              type: 'ui:section',
                              title: 'Merge Rules',
                              isCollapsable: true,
                              children: [
                                {
                                  type: 'ui:helpBlock',
                                  helpText:
                                    "Per-path merge instruction that configures deduping/merging for this entity's fields.",
                                  className: 'u-bumperBottom--sm',
                                },
                                {
                                  type: 'ui:dictionary',
                                  value: 'ingestionFlags.updateBehavior',
                                  keyLabel: 'Path',
                                  addButtonText: 'Add Path',
                                  items: {
                                    type: 'ui:formTable',
                                    className: 'c-dictionaryEntry',
                                    children: [
                                      {
                                        type: 'ui:dictionaryKey',
                                        className: 'c-dictionaryKey',
                                        style: { width: 220 },
                                        itemTemplate: {
                                          type: 'ui:input',
                                          placeholder: 'Path',
                                        },
                                      },
                                      {
                                        type: 'ui:dictionaryValue',
                                        className: 'c-dictionaryValue',
                                        itemTemplate: {
                                          type: 'ui:selectField',
                                          value: '.',
                                          isHorizontalLayout: false,
                                        },
                                      },
                                    ],
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Property Map',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText: 'Maps a dot-path on the target entity to a data-munging expression.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:formulaInput',
                  isVisible: false,
                  formula:
                    'POSSIBLE_PATHS(core_integrations_ingestion_multiEntity_payloadConfig.entities)',
                  transientValue: '_targetPathSuggestions',
                },
                {
                  type: 'ui:dictionary',
                  value: 'core_integrations_ingestion_multiEntity_payloadConfig.propertyMap',
                  header: {
                    type: 'ui:view',
                    className: 'c-dictionaryHeader u-flex c-placeholderColor u-bumperBottom--xs',
                    children: [
                      {
                        type: 'ui:view',
                        className: 'c-dictionaryKey',
                        style: { width: 300 },
                        text: 'Target path',
                      },
                      {
                        type: 'ui:view',
                        className: 'c-dictionaryValue',
                        text: 'Munging expression',
                      },
                    ],
                  },
                  items: {
                    type: 'ui:formTable',
                    className: 'c-dictionaryEntry',
                    children: [
                      {
                        type: 'ui:dictionaryKey',
                        style: { width: 300 },
                        itemTemplate: {
                          type: 'ui:selectField',
                          isCreatable: true,
                          isSearchable: true,
                          placeholder: 'Target dot-path',
                          mapStateToProps: {
                            suggestions: '_targetPathSuggestions',
                          },
                        },
                      },
                      {
                        type: 'ui:dictionaryValue',
                        className: 'c-dictionaryValue',
                        itemTemplate: {
                          type: 'ui:codeMirrorInput',
                          value: '.',
                          isHorizontalLayout: false,
                          language: 'typescript',
                          showStatusLine: false,
                          minHeight: '4.5em',
                          maxHeight: '16em',
                          placeholder: 'Munging expression',
                        },
                      },
                    ],
                  },
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Processing',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Structured Data Access',
                      type: 'ui:checkboxField',
                      value:
                        'core_integrations_ingestion_multiEntity_payloadConfig.preProcessorFlags.enableStructuredDataAccess',
                    },
                    {
                      label: 'Ignore Structured Data Conflict',
                      type: 'ui:checkboxField',
                      value:
                        'core_integrations_ingestion_multiEntity_payloadConfig.preProcessorFlags.ignoreStructuredDataConflict',
                    },
                    {
                      label: 'Calculate Order Key',
                      type: 'ui:checkboxField',
                      value:
                        'core_integrations_ingestion_multiEntity_payloadConfig.preProcessorFlags.orderKey.enabled',
                    },
                    {
                      label: 'Order Key Expression',
                      type: 'ui:inputField',
                      value:
                        'core_integrations_ingestion_multiEntity_payloadConfig.preProcessorFlags.orderKey.expression',
                      placeholder: 'Expression over the first payload entry',
                    },
                  ],
                },
              ],
            },
          ],
        },
        entityCreationPanel: {
          type: 'ui:panel',
          title: 'New Payload Config',
          size: 'sm',
          children: [
            {
              $ref: '#/uiSchema/web/entityDetailSection',
            },
          ],
          mapActionsToProps: {
            onClose: 'handleOnClose',
          },
          panelFooter: {
            type: 'ui:entityFooter',
            mapActionsToProps: {
              onCancelButtonClick: 'handleOnClose',
              onPrimaryButtonClick: 'handleOnSave',
            },
            mapStateToProps: {
              entity: 'entity',
              errors: 'errors',
            },
          },
        },
      },
    },
  },

  // [VD-24632] Local preview of the "Ingestion Configs" view filters before the
  // schema change (schemas/data/views/pages/internalTools/ingestionPipelineConfigs.json)
  // is deployed. Merges the expanded availableFilters onto whatever the server returns.
  'a1b2c3d4-e5f6-7890-abcd-ef1234567890': {
    view: {
      uiSchema: {
        detailPanelDefaultSize: 544,
        addEntityDefaultValue: {
          core_integrations_sftp_ingestion_config: {
            messageType: 'MULTI_ENTITY',
            shouldPreProcessPayload: true,
          },
        },
      },
      state: {
        viewMode: 'listDetail',
        availableFilters: [
          { path: 'precomputation.displayName', label: 'Name', type: 'match' },
          {
            path: 'core_integrations_sftp_ingestion_config.messageType',
            label: 'Message Type',
            type: 'match',
          },
          {
            path: 'core_integrations_sftp_ingestion_config.sourceDirectory',
            label: 'Source Directory',
            type: 'contains',
          },
          {
            path: 'core_integrations_sftp_ingestion_config.firm',
            label: 'Firm',
            type: 'matchEdge',
          },
          { path: 'creationDate', label: 'Creation Date', type: 'range' },
        ],
      },
    },
  },

  // [VD-24637] Local preview of the FTP Connection detail section before the schema's
  // uiSchema change is deployed. Merges uiSchema onto the FTP connection schema entity so the
  // detail panel renders when a connection is opened (e.g. navigate to /entity/<ftpConnectionId>).
  'adcc9fda-1b56-4704-9144-2b81a2aa6e26': {
    metadata: { isCloneable: true },
    uiSchema: {
      web: {
        entityDetailSection: {
          type: 'ui:view',
          children: [
            {
              type: 'ui:section',
              title: 'Connection',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Name',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.name',
                      placeholder: 'Display name for this connection',
                    },
                    {
                      label: 'Description',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.description',
                    },
                    {
                      label: 'Protocol',
                      type: 'ui:selectField',
                      value: 'core_integrations_ftp_connection.protocol',
                    },
                    {
                      label: 'Host',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.host',
                      placeholder: 'e.g. sftp.example.com',
                    },
                    {
                      label: 'Port',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.port',
                      placeholder: 'e.g. 22',
                    },
                    {
                      label: 'Username',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.username',
                    },
                    {
                      label: 'Source Directory',
                      type: 'ui:inputField',
                      value: 'core_integrations_ftp_connection.sourceDirectoryOverride',
                      placeholder: 'Optional path override',
                    },
                  ],
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Credentials',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText:
                    'Provide a password or an RSA private key. Secrets are masked and write-only.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Password',
                      type: 'ui:secretStringField',
                      value: 'core_integrations_ftp_connection.password',
                    },
                    {
                      label: 'RSA Key',
                      type: 'ui:secretStringField',
                      value: 'core_integrations_ftp_connection.rsaKey',
                    },
                  ],
                },
              ],
            },
          ],
        },
        entityCreationPanel: {
          type: 'ui:panel',
          title: 'New FTP Connection',
          size: 'sm',
          children: [
            {
              $ref: '#/uiSchema/web/entityDetailSection',
            },
          ],
          mapActionsToProps: {
            onClose: 'handleOnClose',
          },
          panelFooter: {
            type: 'ui:entityFooter',
            mapActionsToProps: {
              onCancelButtonClick: 'handleOnClose',
              onPrimaryButtonClick: 'handleOnSave',
            },
            mapStateToProps: {
              entity: 'entity',
              errors: 'errors',
            },
          },
        },
      },
    },
  },

  // [VD-24633] Local preview of the SFTP Ingestion Config detail section before the
  // schema's uiSchema change is deployed. Merges uiSchema onto the schema entity so the
  // detail panel renders when a config is opened.
  '67cac340-2662-4184-a3c1-b7a3251d7d68': {
    metadata: { isCloneable: true },
    uiSchema: {
      web: {
        entitySelectOptionCard: {
          type: 'ui:entityPreviewCard',
          data: [
            { type: 'identifier', formula: 'core_integrations_sftp_ingestion_config.firm.displayName' },
            { type: 'status', color: 'default', path: 'core_integrations_sftp_ingestion_config.messageType' },
            {
              type: 'status',
              color: 'blue',
              label: 'Dir',
              path: 'core_integrations_sftp_ingestion_config.sourceDirectory',
            },
            { type: 'status', color: 'default', label: 'ID', path: 'uniqueId' },
          ],
        },
        entitySelectDisplayCard: {
          type: 'ui:entityPreviewCard',
          data: [
            { type: 'identifier', formula: 'core_integrations_sftp_ingestion_config.firm.displayName' },
            { type: 'status', color: 'default', path: 'core_integrations_sftp_ingestion_config.messageType' },
            {
              type: 'status',
              color: 'blue',
              label: 'Dir',
              path: 'core_integrations_sftp_ingestion_config.sourceDirectory',
            },
            { type: 'status', color: 'default', label: 'ID', path: 'uniqueId' },
          ],
        },
        entityDetailSection: {
          type: 'ui:view',
          children: [
            {
              type: 'ui:section',
              title: 'Pipeline',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Message Type',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.messageType',
                    },
                    {
                      label: 'Firm',
                      type: 'ui:entitySelectField',
                      entityType: '/1.0/entities/metadata/firm.json',
                      value: 'core_integrations_sftp_ingestion_config.firm',
                    },
                    {
                      label: 'Source Directory',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.sourceDirectory',
                      placeholder: 'Path on the FTP server to poll',
                    },
                    {
                      label: 'Payload Config',
                      type: 'ui:entitySelectField',
                      entityType:
                        '/1.0/entities/metadata/core_integrations_ingestion_multiEntity_payloadConfig.json',
                      value: 'core_integrations_sftp_ingestion_config.multiEntityPayloadConfig',
                    },
                    {
                      label: 'Pre-Process Payload',
                      type: 'ui:checkboxField',
                      value: 'core_integrations_sftp_ingestion_config.shouldPreProcessPayload',
                    },
                  ],
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'FTP Connections',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText:
                    'FTP Connection entity id per environment. Ids differ per environment and are not resolved or validated here. A job with no entry for the current environment will not start.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:dictionary',
                  value: 'core_integrations_sftp_ingestion_config.srcFtpConnectionMap',
                  keyLabel: 'Environment',
                  valueLabel: 'FTP Connection ID',
                  addButtonText: 'Add Environment',
                  items: {
                    type: 'ui:formTable',
                    className: 'c-dictionaryEntry',
                    children: [
                      {
                        type: 'ui:dictionaryKey',
                        style: { width: 200 },
                        itemTemplate: {
                          type: 'ui:selectField',
                          placeholder: 'Environment (e.g. staging)',
                        },
                      },
                      {
                        type: 'ui:dictionaryValue',
                        className: 'c-dictionaryValue',
                        itemTemplate: {
                          type: 'ui:input',
                          value: 'entityId',
                          isHorizontalLayout: false,
                          placeholder: 'FTP Connection entity id',
                        },
                      },
                    ],
                  },
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Polling & Ordering',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Poll Interval (seconds)',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.period',
                      placeholder: 'e.g. 60',
                    },
                    {
                      label: 'Scan Recursively',
                      type: 'ui:checkboxField',
                      value: 'core_integrations_sftp_ingestion_config.shouldScanRecursively',
                    },
                    {
                      label: 'Max Payloads Per Run',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.maxPayloadsPerRun',
                    },
                    {
                      label: 'Order Key',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.ordering.orderKey',
                    },
                    {
                      label: 'Order',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.ordering.order',
                    },
                    {
                      label: 'Backup Behavior',
                      type: 'ui:selectField',
                      value: 'core_integrations_sftp_ingestion_config.ordering.backupBehavior',
                    },
                    {
                      label: 'Filename Regex',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.filenameRegex',
                      placeholder: 'Required when ordering by datetime',
                    },
                    {
                      label: 'Datetime Pattern',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.datetimePattern',
                      placeholder: 'Required when ordering by datetime',
                    },
                  ],
                },
              ],
            },
            {
              type: 'ui:section',
              title: 'Retry & Notifications',
              isCollapsable: true,
              children: [
                {
                  type: 'ui:helpBlock',
                  helpText:
                    'Retries cover system-level failures only (e.g. 423), not payload parsing or ingestion errors.',
                  className: 'u-bumperBottom--sm',
                },
                {
                  type: 'ui:formTable',
                  className: 'u-bumperBottom',
                  children: [
                    {
                      label: 'Retry On Failure',
                      type: 'ui:checkboxField',
                      value:
                        'core_integrations_sftp_ingestion_config.ingestionRetryBehavior.shouldRetry',
                    },
                    {
                      label: 'Retry Attempts',
                      type: 'ui:inputField',
                      value:
                        'core_integrations_sftp_ingestion_config.ingestionRetryBehavior.numAttempts',
                    },
                    {
                      label: 'Email Ingestion Enabled',
                      type: 'ui:checkboxField',
                      value: 'core_integrations_sftp_ingestion_config.email.enabled',
                    },
                    {
                      label: 'Mailgun Prefix',
                      type: 'ui:inputField',
                      value: 'core_integrations_sftp_ingestion_config.email.mailgunPrefix',
                      placeholder: 'Payloads to <prefix>@mg.withvector.com trigger ingestion',
                    },
                  ],
                },
              ],
            },
          ],
        },
        entityCreationPanel: {
          type: 'ui:panel',
          title: 'New SFTP Ingestion Config',
          size: 'sm',
          children: [
            {
              $ref: '#/uiSchema/web/entityDetailSection',
            },
          ],
          mapActionsToProps: {
            onClose: 'handleOnClose',
          },
          panelFooter: {
            type: 'ui:entityFooter',
            mapActionsToProps: {
              onCancelButtonClick: 'handleOnClose',
              onPrimaryButtonClick: 'handleOnSave',
            },
            mapStateToProps: {
              entity: 'entity',
              errors: 'errors',
            },
          },
        },
      },
    },
  },
}

/** Normalize a map entry to a concrete override; a bare content object is treated as a merge. */
export function normalizeDebugOverride(entry: EntityDebugOverrideEntry): IEntityDebugOverride {
  const mode = (entry as IEntityDebugOverride)?.mode
  if ((mode === 'merge' || mode === 'replace') && 'content' in (entry as any)) {
    return entry as IEntityDebugOverride
  }
  return { mode: 'merge', content: entry }
}
